/**
 * A platform administrator looking at one client's workspace, read only.
 *
 * Kept per tab in sessionStorage and sent as two headers on every request; the
 * server decides whether the view is allowed and refuses every write while it
 * is on. Leaving reloads the app so nothing read as the client stays cached.
 */
import { client } from "@/shared/api/client.gen";

export type ViewedAccount = {
  readonly tenantId: string;
  readonly accountId: string;
  readonly name: string;
};

const KEY = "skryptos.view-as-client";
export const VIEW_TENANT_HEADER = "x-skryptos-view-tenant";
export const VIEW_ACCOUNT_HEADER = "x-skryptos-view-account";

function isViewed(value: unknown): value is ViewedAccount {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.tenantId === "string" &&
    record.tenantId !== "" &&
    typeof record.accountId === "string" &&
    record.accountId !== "" &&
    typeof record.name === "string"
  );
}

export function readViewAs(): ViewedAccount | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isViewed(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Forget the view without navigating: used before signing in or out. */
export function clearViewAs(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: nothing was stored, so nothing is sent.
  }
}

export function startViewAs(account: ViewedAccount): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(account));
  } catch {
    return;
  }
  window.location.assign("/app");
}

export function stopViewAs(): void {
  clearViewAs();
  window.location.assign("/app/clients");
}

let installed = false;

/** Attach the view headers to every API request while a view is active. */
export function installViewAsHeaders(): void {
  if (installed) return;
  installed = true;
  client.interceptors.request.use((request) => {
    const viewed = readViewAs();
    if (viewed !== null) {
      request.headers.set(VIEW_TENANT_HEADER, viewed.tenantId);
      request.headers.set(VIEW_ACCOUNT_HEADER, viewed.accountId);
    }
    return request;
  });
}
